import { format, parseISO } from 'date-fns';
import {
  Activity,
  Brain,
  Calendar,
  Check,
  ChevronDown,
  ClipboardList,
  FileText,
  HelpCircle,
  Loader2,
  MessageSquare,
  X,
} from 'lucide-react';
import { useState } from 'react';

import { MarkdownText } from '@/components/common/MarkdownText';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import {
  useGetAIExtractionQuery,
  useGetTreatmentCheckInQuery,
} from '@/services/api/treatmentService';
import {
  CheckInObjectiveResponse,
  CheckInObjectiveType,
  type TreatmentCheckInObjective,
  type TreatmentCheckInProviderUpdate,
} from '@/services/types/treatment-types';

import { CheckInAuthor } from '../CheckInAuthor';
import { CheckInStatusBadge } from '../CheckInStatusBadge';
import { treatmentStatusInfo } from '../constants';
import { FlaggedIssuesList } from './FlaggedIssuesList';

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const painColor = (level: number): string => {
  if (level <= 2) return 'bg-emerald-500 text-white';
  if (level <= 4) return 'bg-yellow-500 text-white';
  if (level <= 6) return 'bg-amber-500 text-white';
  if (level <= 8) return 'bg-orange-500 text-white';
  return 'bg-red-600 text-white';
};

const responseLabel = (r: CheckInObjectiveResponse): string => {
  switch (r) {
    case CheckInObjectiveResponse.YES:
      return 'Yes';
    case CheckInObjectiveResponse.NO:
      return 'No';
    case CheckInObjectiveResponse.COULD_NOT_COLLECT:
      return 'Unsure';
    default:
      return 'Pending';
  }
};

const responseStyle = (r: CheckInObjectiveResponse): string => {
  switch (r) {
    case CheckInObjectiveResponse.YES:
      return 'bg-emerald-500 text-white';
    case CheckInObjectiveResponse.NO:
      return 'bg-red-500 text-white';
    case CheckInObjectiveResponse.COULD_NOT_COLLECT:
      return 'bg-amber-500 text-white';
    default:
      return 'bg-zinc-100 text-zinc-600';
  }
};

const ResponseIcon = ({
  response,
}: {
  response: CheckInObjectiveResponse;
}) => {
  switch (response) {
    case CheckInObjectiveResponse.YES:
      return <Check className="h-3 w-3" />;
    case CheckInObjectiveResponse.NO:
      return <X className="h-3 w-3" />;
    case CheckInObjectiveResponse.COULD_NOT_COLLECT:
      return <HelpCircle className="h-3 w-3" />;
    default:
      return null;
  }
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Read-only pain level display matching PainLevelSelector style */
const PainLevelDisplay = ({
  level,
  note,
}: {
  level: number | null;
  note: string;
}) => {
  const PAIN_LEVELS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;
  const isCouldNotAssess = level === null && note;

  return (
    <div className="bg-white rounded-lg border px-3 py-2">
      <div className="flex items-center gap-3">
        <span className="text-xs font-semibold text-zinc-900 shrink-0">
          Pain level
        </span>
        {level != null ? (
          <div className="flex items-center gap-0.5">
            {PAIN_LEVELS.map((l) => (
              <div
                key={l}
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded border border-zinc-200 text-[11px] font-medium',
                  level === l ? painColor(l) : 'bg-white text-zinc-600',
                )}
              >
                {l}
              </div>
            ))}
          </div>
        ) : isCouldNotAssess ? (
          <span className="text-xs font-medium text-amber-700 bg-amber-50 rounded px-1.5 py-0.5 ring-1 ring-amber-600/20 ring-inset">
            Could not assess
          </span>
        ) : (
          <span className="text-xs font-medium text-zinc-500">Not recorded</span>
        )}
      </div>
      {isCouldNotAssess && note && (
        <p className="mt-1.5 text-xs font-medium text-zinc-600 italic pl-0.5">{note}</p>
      )}
    </div>
  );
};

/** Read-only objective row matching ObjectiveCard style */
const ObjectiveRow = ({
  objective,
}: {
  objective: TreatmentCheckInObjective;
}) => {
  const isStillTreating =
    objective.objective_type === CheckInObjectiveType.CONFIRM_STILL_TREATING;
  const isFirstVisit =
    objective.objective_type === CheckInObjectiveType.CONFIRM_FIRST_VISIT;

  return (
    <div className="mx-[-4px] rounded-md px-1 py-1">
      <div className="flex items-center justify-between gap-2 min-h-[28px]">
        <span className="text-xs font-medium text-zinc-700 shrink-0">
          {objective.label}
        </span>
        <div
          className={cn(
            'inline-flex items-center gap-0.5 rounded-md px-2 py-1 text-xs font-medium',
            responseStyle(objective.response),
          )}
        >
          <ResponseIcon response={objective.response} />
          <span>{responseLabel(objective.response)}</span>
        </div>
      </div>
      {objective.response_note &&
        (objective.response === CheckInObjectiveResponse.COULD_NOT_COLLECT ||
          isStillTreating ||
          isFirstVisit) && (
          <p className="mt-0.5 text-[11px] font-medium text-zinc-500 italic pl-1">
            {objective.response_note}
          </p>
        )}
    </div>
  );
};

/** Provider update card with objectives */
const ProviderSection = ({
  update,
  objectives,
}: {
  update: TreatmentCheckInProviderUpdate;
  objectives: TreatmentCheckInObjective[];
}) => {
  const statusInfo = update.new_treatment_status
    ? treatmentStatusInfo[update.new_treatment_status]
    : null;
  const Icon = statusInfo?.icon;

  return (
    <div className="bg-white rounded-lg border p-3 space-y-2">
      {/* Provider header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-zinc-900">
            {update.provider_name ?? 'Unknown Provider'}
          </span>
          {update.is_first_appointment && (
            <Badge
              variant="outline"
              className="text-[10px] text-blue-600 border-blue-300 px-1 py-0"
            >
              First visit
            </Badge>
          )}
        </div>
        {statusInfo && Icon && (
          <div className="flex items-center gap-1">
            <Icon className={cn('h-3.5 w-3.5', statusInfo.color)} />
            <span className="text-xs font-medium text-zinc-700">
              {statusInfo.label}
            </span>
          </div>
        )}
      </div>

      {/* Metadata row */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-medium text-zinc-500">
        {update.new_treatment_status_occurred_at && (
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {format(parseISO(update.new_treatment_status_occurred_at), 'PP')}
          </span>
        )}
        {update.new_treatment_frequency_text && (
          <span className="flex items-center gap-1">
            <Activity className="h-3 w-3" />
            {update.new_treatment_frequency_text}
          </span>
        )}
        {update.new_first_treatment_date && (
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            First visit:{' '}
            {format(parseISO(update.new_first_treatment_date), 'PP')}
          </span>
        )}
      </div>

      {/* Objectives for this provider */}
      {objectives.length > 0 && (
        <div className="border-t pt-2 space-y-0.5">
          <div className="flex items-center gap-1 mb-1">
            <ClipboardList className="h-3 w-3 text-zinc-400" />
            <span className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">
              Objectives
            </span>
          </div>
          {objectives.map((obj) => (
            <ObjectiveRow key={obj.id} objective={obj} />
          ))}
        </div>
      )}

      {/* Treatment notes */}
      {update.new_treatment_notes && (
        <div className="border-t pt-2">
          <div className="flex items-center gap-1 mb-1">
            <MessageSquare className="h-3 w-3 text-zinc-400" />
            <span className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">
              Notes
            </span>
          </div>
          <p className="text-xs font-medium text-zinc-700 italic">
            {update.new_treatment_notes}
          </p>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface TreatmentCheckInDetailsProps {
  matterId: string;
  checkInId: string;
}

export const TreatmentCheckInDetails = ({
  matterId,
  checkInId,
}: TreatmentCheckInDetailsProps) => {
  const {
    data: checkIn,
    isLoading,
    error,
  } = useGetTreatmentCheckInQuery({ matterId, checkInId });

  const {
    data: aiExtraction,
    isLoading: isLoadingAI,
    error: aiError,
  } = useGetAIExtractionQuery({ matterId, checkInId }, { skip: !checkIn?.id });

  const [isAIOpen, setIsAIOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (error || !checkIn) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <p className="text-sm font-medium text-zinc-500">
          Failed to load check-in details.
        </p>
      </div>
    );
  }

  // Group objectives by provider
  const providerObjectives = new Map<string, TreatmentCheckInObjective[]>();
  const matterObjectives: TreatmentCheckInObjective[] = [];
  checkIn.objectives?.forEach((obj) => {
    if (obj.provider_id) {
      const existing = providerObjectives.get(obj.provider_id) ?? [];
      existing.push(obj);
      providerObjectives.set(obj.provider_id, existing);
    } else {
      matterObjectives.push(obj);
    }
  });

  const hasAI =
    !aiError &&
    aiExtraction &&
    aiExtraction.extraction_status === 'completed';

  return (
    <div className="max-w-3xl mx-auto space-y-3">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckInStatusBadge status={checkIn.status} />
                    <span className="text-xs font-medium text-zinc-500">
                      {checkIn.check_in_time
              ? new Date(checkIn.check_in_time).toLocaleString()
              : new Date(checkIn.created_at).toLocaleString()}
          </span>
        </div>
        {checkIn.user_id && <CheckInAuthor userId={checkIn.user_id} />}
      </div>

      {/* Source transcript banner */}
      {checkIn.source_transcript_id && (
        <div className="flex items-center justify-between px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs">
          <div className="flex items-center gap-1.5 text-blue-800">
            <FileText className="h-3.5 w-3.5" />
            <span className="font-medium">Source Call Transcript</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs text-blue-700"
            disabled={!checkIn.source_conversation_id}
            onClick={() =>
              checkIn.source_conversation_id &&
              window.open(
                `/call/${checkIn.source_conversation_id}`,
                '_blank',
              )
            }
          >
            View
          </Button>
        </div>
      )}

      {/* AI Overview - collapsible */}
      <Collapsible open={isAIOpen} onOpenChange={setIsAIOpen}>
        <div className="border border-purple-200 rounded-lg bg-purple-50/50">
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="w-full flex items-center justify-between hover:bg-purple-50 px-3 py-2"
            >
              <div className="flex items-center gap-2">
                <Brain className="h-3.5 w-3.5 text-purple-600" />
                <span className="text-xs font-medium text-purple-900">
                  AI-Extracted Summary
                </span>
                <Badge
                  variant="outline"
                  className="text-[10px] text-purple-700 border-purple-300 px-1 py-0"
                >
                  AI
                </Badge>
              </div>
              <ChevronDown
                className={cn(
                  'h-3.5 w-3.5 text-purple-600 transition-transform',
                  isAIOpen && 'rotate-180',
                )}
              />
            </Button>
          </CollapsibleTrigger>

          <CollapsibleContent className="px-3 pb-3 space-y-2">
            {isLoadingAI ? (
              <div className="flex items-center gap-2 text-xs font-medium text-purple-700 py-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span>Loading AI extraction...</span>
              </div>
            ) : !hasAI ? (
              <p className="text-xs font-medium text-purple-600 py-1">
                AI extraction not available yet. Extraction runs after
                transcript completes (~15-20 min).
              </p>
            ) : (
              <>
                {/* Claimant Status */}
                {aiExtraction.how_claimant_feels && (
                  <div className="py-1.5 bg-white border border-purple-100 rounded px-2.5 text-xs space-y-1">
                    <p className="font-semibold text-purple-900 text-[11px] uppercase tracking-wide">
                      Claimant Status
                    </p>
                    {aiExtraction.how_claimant_feels.summary && (
                                            <p className="font-medium text-zinc-800">
                                              {aiExtraction.how_claimant_feels.summary}
                      </p>
                    )}
                    {aiExtraction.how_claimant_feels.severity?.reported &&
                      aiExtraction.how_claimant_feels.severity.value !=
                      null && (
                      <p className="text-zinc-900 font-medium">
                        Pain:{' '}
                        {aiExtraction.how_claimant_feels.severity.value}
                        /10
                        {aiExtraction.how_claimant_feels.severity
                          .descriptor &&
                          ` (${aiExtraction.how_claimant_feels.severity.descriptor})`}
                      </p>
                    )}
                    {aiExtraction.how_claimant_feels.symptoms_reported
                      .length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {aiExtraction.how_claimant_feels.symptoms_reported.map(
                          (symptom) => (
                            <Badge
                              key={symptom}
                              variant="secondary"
                              className="text-[10px] px-1.5 py-0"
                            >
                              {symptom}
                            </Badge>
                          ),
                        )}
                      </div>
                    )}
                    {aiExtraction.how_claimant_feels.functional_impact
                      ?.reported &&
                      aiExtraction.how_claimant_feels.functional_impact
                        .details.length > 0 && (
                                                <p className="font-medium text-zinc-600">
                                                  <span className="font-medium">Limitations:</span>{' '}
                          {aiExtraction.how_claimant_feels.functional_impact.details.join(
                            ', ',
                          )}
                        </p>
                      )}
                  </div>
                )}

                {/* Provider Extractions */}
                {aiExtraction.provider_extractions.length > 0 && (
                  <div className="space-y-1">
                    {aiExtraction.provider_extractions.map(
                      (provider, idx) => (
                        <div
                          key={idx}
                          className="py-1.5 bg-white border border-purple-100 rounded px-2.5 text-xs"
                        >
                          <p className="font-semibold text-purple-900">
                            {provider.provider_name}
                            {provider.provider_type && (
                              <span className="font-medium text-zinc-500 ml-1">
                                {provider.provider_type}
                              </span>
                            )}
                            {provider.treatment_status && (
                              <span className="font-medium text-zinc-500">
                                {' '}
                                &middot; {provider.treatment_status}
                              </span>
                            )}
                          </p>
                          {provider.treatment_notes && (
                            <p className="font-medium text-zinc-700 italic mt-0.5">
                              {provider.treatment_notes}
                            </p>
                          )}
                          {provider.treatment_frequency && (
                                                        <p className="font-medium text-zinc-600 mt-0.5">
                                                          Frequency: {provider.treatment_frequency}
                            </p>
                          )}
                          {provider.next_appointment?.scheduled && (
                                                        <p className="font-medium text-zinc-600 mt-0.5">
                                                          Next:{' '}
                              {
                                provider.next_appointment
                                  .date_or_timeframe
                              }
                              {provider.next_appointment.purpose &&
                                ` (${provider.next_appointment.purpose})`}
                            </p>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                )}

                {/* Treatment Completed */}
                {aiExtraction.treatment_completed.reported &&
                  aiExtraction.treatment_completed.items.length > 0 && (
                    <div className=                    "text-[11px] font-medium text-zinc-800 bg-white border border-purple-100 rounded px-2.5 py-1.5">
                                          <span className="font-semibold">Completed:</span>{' '}
                      {aiExtraction.treatment_completed.items
                        .map(
                          (i) =>
                            `${i.type}${i.provider ? ` with ${i.provider}` : ''}${i.completion_date ? ` (ended ${i.completion_date})` : ''}`,
                        )
                        .join('; ')}
                    </div>
                  )}

                {/* Treatment Ongoing */}
                {aiExtraction.treatment_ongoing
                  .ongoing_treatment_reported &&
                  aiExtraction.treatment_ongoing.ongoing_items.length >
                    0 && (
                    <div className=                    "text-[11px] font-medium text-zinc-800 bg-white border border-purple-100 rounded px-2.5 py-1.5">
                                          <span className="font-semibold">Ongoing:</span>{' '}
                      {aiExtraction.treatment_ongoing.ongoing_items
                        .map(
                          (i) =>
                            `${i.type}${i.frequency ? ` - ${i.frequency}` : ''}${i.provider ? ` with ${i.provider}` : ''}`,
                        )
                        .join('; ')}
                    </div>
                  )}
              </>
            )}
          </CollapsibleContent>
        </div>
      </Collapsible>

      {/* Pain Level */}
      <PainLevelDisplay
        level={checkIn.pain_level}
        note={checkIn.pain_level_note}
      />

      {/* Matter-level objectives (call objectives) */}
      {matterObjectives.length > 0 && (
        <div className="bg-white rounded-lg border p-3 space-y-1">
          <div className="flex items-center gap-1 mb-1">
            <ClipboardList className="h-3.5 w-3.5 text-zinc-400" />
            <span className="text-xs font-semibold text-zinc-700">
              Call Objectives
            </span>
          </div>
          {matterObjectives.map((obj) => (
            <ObjectiveRow key={obj.id} objective={obj} />
          ))}
        </div>
      )}

      {/* Provider Updates + their objectives */}
      {checkIn.provider_updates && checkIn.provider_updates.length > 0 ? (
        checkIn.provider_updates.map((update) => (
          <ProviderSection
            key={update.id}
            update={update}
            objectives={
              update.provider_id
                ? (providerObjectives.get(update.provider_id) ?? [])
                : []
            }
          />
        ))
      ) : (
        <div className="bg-white rounded-lg border p-3 text-center">
                    <p className="text-xs font-medium text-zinc-500">
                      No provider updates recorded
          </p>
        </div>
      )}

      {/* General Notes */}
      {checkIn.general_notes && (
        <div className="bg-white rounded-lg border p-3">
          <div className="flex items-center gap-1 mb-1">
            <MessageSquare className="h-3.5 w-3.5 text-zinc-400" />
            <span className="text-xs font-semibold text-zinc-700">
              General Notes
            </span>
          </div>
          <MarkdownText
            content={checkIn.general_notes}
            className="text-xs font-medium text-zinc-700"
            plainTextClassName="whitespace-pre-wrap"
            markdownClassName="prose prose-xs max-w-none prose-p:my-0.5 prose-p:text-xs"
          />
        </div>
      )}

      {/* Flagged Issues */}
      <FlaggedIssuesList matterId={matterId} checkInId={checkInId} />
    </div>
  );
};
